/**
 * Lightweight funnel + placement-time analytics tracker.
 *
 * - Fire-and-forget: never blocks UI, never throws.
 * - Stores a stable session_id in localStorage so anonymous users can be
 *   followed from menu_view → cart_add → checkout_view → payment.
 * - Stamps `cart_started_at` on the first cart_add of a session and exposes
 *   `getCartDurationSeconds()` so the order create call can persist
 *   `placement_duration_seconds`.
 */
import axios from 'axios';

const API = process.env.REACT_APP_BACKEND_URL;
const SID_KEY = 'pbc_funnel_sid';
const CART_START_KEY = 'pbc_cart_started_at';

function getSessionId() {
  let sid = localStorage.getItem(SID_KEY);
  if (!sid) {
    sid = `sid_${Date.now().toString(36)}_${Math.random().toString(36).slice(2, 10)}`;
    localStorage.setItem(SID_KEY, sid);
  }
  return sid;
}

function authHeaders() {
  const t = localStorage.getItem('token');
  return t ? { Authorization: `Bearer ${t}` } : {};
}

/**
 * Read the currently-selected branch ID from localStorage. Returns ''
 * when nothing is set (first-time visitor, deep-link to checkout, etc.).
 * Caller-side `branch_id` in `trackEvent`'s `extra` ALWAYS wins over
 * this fallback so a page with explicit branch state (Checkout, Menu,
 * Cart) can stay consistent even if localStorage hasn't been written yet.
 */
function readBranchIdFromStorage() {
  try {
    const b = JSON.parse(localStorage.getItem('selectedBranch') || 'null');
    return b?.id || '';
  } catch { return ''; }
}

/**
 * Fire-and-forget event ping. Never throws, never blocks.
 * @param {string} event one of: menu_view | cart_add | checkout_view | place_order_clicked | payment_success | payment_failed | abandoned | app_open | session_heartbeat | discovery_tap
 * @param {object} extra optional `{ branch_id?, metadata? }` — when `branch_id` is omitted
 *                       the helper reads from `localStorage.selectedBranch`. Pass it
 *                       explicitly from any caller that has React state for the
 *                       branch (Checkout, Menu, Cart) so the event is correctly
 *                       branch-scoped even before localStorage is populated.
 *                       Per-branch funnel + DAU/WAU/MAU break otherwise.
 */
export function trackEvent(event, extra = {}) {
  try {
    const branch_id = extra.branch_id || readBranchIdFromStorage();
    const payload = {
      event,
      session_id: getSessionId(),
      branch_id,
      metadata: extra.metadata || {},
    };
    // Stamp cart_started_at on first cart_add
    if (event === 'cart_add' && !localStorage.getItem(CART_START_KEY)) {
      localStorage.setItem(CART_START_KEY, String(Date.now()));
    }
    // Reset cart_started_at after payment_success
    if (event === 'payment_success') {
      localStorage.removeItem(CART_START_KEY);
    }
    axios.post(`${API}/api/analytics/track`, payload, { headers: authHeaders(), timeout: 4000 })
      .catch(() => {/* swallow — analytics must never break UX */});
  } catch {/* never throw from tracker */}
}

/**
 * Fire a `discovery_tap` origination event — the tap that STARTS a product
 * discovery journey. One call per journey-starting surface:
 * home_quick_pill | home_category_tile | home_bestseller | home_spotlight |
 * home_reorder | home_combo | home_search_full | home_search_suggestion |
 * home_view_all | menu_search_suggestion | menu_fab | nav_menu_tab |
 * nav_offers_tab | offers_page | offers_free_coffee
 * The origination study joins each session's FIRST discovery_tap against its
 * first cart_add (session_id links them).
 * @param {string} surface one of the surface keys above
 * @param {string} [detail] optional context (category name, item name, query)
 * @param {string} [branchId] explicit branch id; falls back to localStorage in trackEvent
 */
export function trackDiscovery(surface, detail = '', branchId = '') {
  trackEvent('discovery_tap', {
    ...(branchId ? { branch_id: branchId } : {}),
    metadata: detail ? { surface, detail } : { surface },
  });
}

/** Seconds since the first cart_add of this session. 0 if not set. */
export function getCartDurationSeconds() {
  const ts = parseInt(localStorage.getItem(CART_START_KEY) || '0', 10);
  if (!ts) return 0;
  return Math.max(0, Math.round((Date.now() - ts) / 1000));
}

/** Reset the cart timer (e.g. on cart clear after a successful order). */
export function resetCartTimer() {
  localStorage.removeItem(CART_START_KEY);
}

// ---- App-engagement tracking (powers DAU/WAU/MAU + avg session time) ----

const APP_OPEN_KEY = 'pbc_last_app_open';
const APP_OPEN_DEDUPE_MS = 30 * 60 * 1000; // 30-min new-session threshold
const HEARTBEAT_INTERVAL_MS = 60 * 1000;

let heartbeatTimer = null;

/**
 * Fire an `app_open` event when the app gains focus and the last open is
 * stale enough to count as a new session. Idempotent within the dedupe window.
 * Forwards `branch_id` from `localStorage.selectedBranch` so DAU/WAU/MAU
 * filtered by branch on the admin dashboard return non-zero per-branch counts
 * (otherwise the backend `funnel_events` aggregation `$match`es on empty
 * `branch_id` when a branch filter is set and collapses to ~0).
 */
export function trackAppOpen() {
  try {
    const last = parseInt(localStorage.getItem(APP_OPEN_KEY) || '0', 10);
    const now = Date.now();
    if (now - last < APP_OPEN_DEDUPE_MS) return;
    localStorage.setItem(APP_OPEN_KEY, String(now));
    trackEvent('app_open', { branch_id: readBranchIdFromStorage() });
  } catch {/* never throw */}
}

/**
 * Periodic ping while the document is visible. Lets the backend derive
 * session duration as max(event ts) - min(event ts) per session_id.
 * Pings stop while the tab is hidden and resume on visibility change.
 * Forwards `branch_id` from localStorage so admin DAU/WAU/MAU per branch
 * stays accurate as the user navigates between branches mid-session.
 */
export function startSessionHeartbeat() {
  if (heartbeatTimer) return;
  const tick = () => {
    if (typeof document !== 'undefined' && document.visibilityState === 'visible') {
      trackEvent('session_heartbeat', { branch_id: readBranchIdFromStorage() });
    }
  };
  heartbeatTimer = setInterval(tick, HEARTBEAT_INTERVAL_MS);
  if (typeof document !== 'undefined') {
    document.addEventListener('visibilitychange', () => {
      if (document.visibilityState === 'visible') trackAppOpen();
    });
  }
}

/**
 * Fire an `abandoned` event when the customer leaves with a non-empty
 * cart and no payment_success in this session. Designed to be called
 * from a `beforeunload` listener or from Cashfree paymentDropped /
 * paymentClosed callbacks where we *know* the payment was abandoned.
 *
 * Uses `navigator.sendBeacon` when available so the event survives the
 * page unload (the standard axios call would be cancelled mid-flight by
 * the browser). Falls back to the regular `trackEvent` path on platforms
 * without sendBeacon.
 *
 * @param {object} extra optional metadata (e.g. `{branch_id, reason, cart_size}`)
 */
export function trackAbandoned(extra = {}) {
  try {
    const branch_id = extra.branch_id || readBranchIdFromStorage();
    const payload = {
      event: 'abandoned',
      session_id: getSessionId(),
      branch_id,
      metadata: extra.metadata || {},
    };
    if (typeof navigator !== 'undefined' && typeof navigator.sendBeacon === 'function') {
      try {
        const blob = new Blob([JSON.stringify(payload)], { type: 'application/json' });
        const ok = navigator.sendBeacon(`${API}/api/analytics/track`, blob);
        if (ok) return;
      } catch {/* fall through */}
    }
    // Fallback when sendBeacon isn't available — fire-and-forget axios.
    trackEvent('abandoned', { branch_id, metadata: extra.metadata });
  } catch {/* never throw */}
}
